import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';

const Cart = ({ isOpen, onClose }) => {
  const { 
    cart, 
    getCartTotal, 
    getCartCount, 
    updateQuantity, 
    removeFromCart,
    getGrandTotal
  } = useCart();
  
  const subtotal = getCartTotal();
  const shipping = subtotal > 150000 ? 0 : 5000;
  const total = getGrandTotal() + shipping;

  // ─────────────────────────────────────────────────────────
  //  PERFECT HEART – subtle, filled, floating (Adjusted for Light BG)
  // ─────────────────────────────────────────────────────────
  const PerfectHeart = ({ size, color, opacity, rotate, delay }) => {
    const heartPath = "M12,4 C8,-2 0,0 0,7 C0,14 12,20 12,20 C12,20 24,14 24,7 C24,0 16,-2 12,4 Z";

    return (
      <motion.svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ rotate }}
        initial={{ opacity: 0, y: 0, scale: 0.9 }}
        animate={{ 
          opacity: 1,
          y: [0, -6, 0],
          scale: 1
        }}
        transition={{
          opacity: { delay, duration: 0.6, ease: "easeOut" },
          scale: { delay, duration: 0.6, ease: "easeOut" },
          y: {
            duration: 4 + Math.random() * 3,
            repeat: Infinity,
            ease: "easeInOut",
            delay: delay + 0.8
          }
        }}
      >
        <path
          d={heartPath}
          fill={color}
          fillOpacity={opacity}
        />
      </motion.svg>
    );
  };

  const CartHearts = () => {
    const [elements, setElements] = useState([]);

    useEffect(() => {
      const isMobile = window.innerWidth < 768;
      const total = isMobile
        ? Math.floor(Math.random() * 2) + 1   
        : Math.floor(Math.random() * 2) + 2; 
      
      const leftCount = Math.ceil(total / 2);
      const rightCount = Math.floor(total / 2);
      
      const minSize = isMobile ? 16 : 20;
      const maxSize = isMobile ? 30 : 40;
      
      const newElements = [];

      for (let i = 0; i < leftCount; i++) {
        newElements.push({
          id: `CART-L-${i}`,
          x: Math.random() * 15,
          y: Math.random() * 100,
          size: Math.floor(Math.random() * (maxSize - minSize + 1)) + minSize,
          rotate: Math.random() * 360,
          opacity: Math.random() * 0.1 + 0.05, // Even more subtle on white
          color: `rgba(190, 24, 93, ${Math.random() * 0.15 + 0.15})`,
          delay: i * 0.15,
        });
      }

      for (let i = 0; i < rightCount; i++) {
        newElements.push({
          id: `CART-R-${i}`,
          x: Math.random() * 15 + 85,
          y: Math.random() * 100,
          size: Math.floor(Math.random() * (maxSize - minSize + 1)) + minSize,
          rotate: Math.random() * 360,
          opacity: Math.random() * 0.1 + 0.05,
          color: `rgba(190, 24, 93, ${Math.random() * 0.15 + 0.15})`,
          delay: (leftCount + i) * 0.15,
        });
      }

      setElements(newElements);
    }, []);

    return (
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        {elements.map((el) => (
          <div
            key={el.id}
            className="absolute"
            style={{
              left: `${el.x}%`,
              top: `${el.y}%`,
              transform: `translate(-50%, -50%)`,
            }}
          >
            <PerfectHeart
              size={el.size}
              color={el.color}
              opacity={el.opacity}
              rotate={el.rotate}
              delay={el.delay}
            />
          </div>
        ))}
      </div>
    );
  };
  // ─────────────────────────────────────────────────────────

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Soft Light Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] transition-all duration-700"
          />

          {/* Cart Sidebar: Pure White + Subtle Hearts */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', ease: [0.19, 1, 0.22, 1], duration: 0.8 }}
            className="fixed top-0 right-0 h-full w-full md:w-[480px] bg-white border-l border-black/5 z-[70] flex flex-col antialiased text-black shadow-2xl"
          >
            {/* ✦ CART HEARTS – EXTREMELY SUBTLE ✦ */}
            <CartHearts />

            {/* Header: Boutique Style */}
            <div className="relative z-10 flex items-center justify-between px-8 py-10 border-b border-black/5">
              <div className="space-y-2">
                <h2 className="text-3xl uppercase tracking-wider font-light text-black">Your Cart</h2>
                <div className="flex items-center gap-2">
                   <span className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-pulse" />
                   <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-medium">
                    {getCartCount()} {getCartCount() === 1 ? 'Curated Piece' : 'Curated Pieces'}
                  </p>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="group p-2 transition-transform duration-500 hover:rotate-90 text-neutral-400 hover:text-black"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="square" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Cart Items */}
            <div className="relative z-10 flex-1 overflow-y-auto px-8 py-4 custom-scrollbar">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center space-y-10">
                  <div className="w-12 h-[1px] bg-black/10" />
                  <p className="uppercase tracking-[0.2em] text-[11px] text-neutral-500 font-medium">Empty Selection</p>
                  <button 
                    onClick={onClose}
                    className="text-[10px] uppercase tracking-wider border-b border-black/20 pb-2 text-pink-600 hover:text-black hover:border-black transition-all duration-500 font-medium"
                  >
                    Explore Shop
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-black/5">
                  {cart.map((item) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="py-10 flex gap-6 group"
                    >
                      {/* Image Container */}
                      <div className="w-24 h-32 overflow-hidden bg-neutral-100 relative border border-black/5">
                        <img
                          src={item.images?.[0]}
                          alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-all duration-1000"
                        />
                      </div>
                      
                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-xs uppercase tracking-wider font-medium text-black mb-2 group-hover:text-pink-600 transition-colors">{item.name}</h4>
                            <p className="text-[10px] text-neutral-400 uppercase tracking-wider font-medium">Code: {item.productCode || 'Luxe-01'}</p>
                          </div>
                          <button onClick={() => removeFromCart(item.id)} className="text-neutral-300 hover:text-pink-600 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="square" strokeWidth={1} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className="flex justify-between items-end">
                          <div className="flex items-center space-x-6 border border-black/10 px-4 py-2 bg-neutral-50">
                            <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="text-sm text-neutral-500 hover:text-pink-600 transition-colors">-</button>
                            <span className="text-xs uppercase tracking-wider w-4 text-center font-semibold text-black">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="text-sm text-neutral-500 hover:text-pink-600 transition-colors">+</button>
                          </div>
                          <p className="text-sm tracking-wider font-medium text-black">
                            ₦{(item.price * item.quantity).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer Summary */}
            {cart.length > 0 && (
              <div className="relative z-10 p-8 pb-12 border-t border-black/5 space-y-8 bg-white/90 backdrop-blur-xl">
                <div className="space-y-5">
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Subtotal</span>
                    <span className="text-sm tracking-wider font-medium text-black">₦{subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Shipping</span>
                    <span className="text-[10px] tracking-wider text-pink-600 font-semibold">
                      {shipping === 0 ? 'Complimentary' : `₦${shipping.toLocaleString()}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pt-6 border-t border-black/5">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-black">Total</span>
                    <span className="text-xl tracking-wider font-normal text-pink-600">₦{total.toLocaleString()}</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <Link to="/checkout" onClick={onClose}>
                    <button className="relative w-full overflow-hidden group bg-black text-white py-5 uppercase tracking-[0.2em] text-[11px] font-semibold transition-all duration-500">
                      <span className="relative z-10 group-hover:text-white transition-colors duration-500">Checkout</span>
                      <div className="absolute inset-0 bg-pink-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                    </button>
                  </Link>
                  <button 
                    onClick={onClose}
                    className="w-full py-2 text-[10px] uppercase tracking-wider text-neutral-500 hover:text-pink-600 transition-all duration-300 font-medium"
                  >
                    Continue Shopping
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Cart;